import { useMutation } from '@apollo/client';
import { AnimatePresence, LayoutGroup, motion } from 'framer-motion';
import React, {
  ChangeEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  ChatHeader,
  type ChatMessage,
  ChatMessageBubble,
  ChatThread,
  ChatThreadCard,
  type Citation,
  CitationsSection,
  PdfContext,
  type PdfContextState,
  PdfPasswordModal,
  PdfPreviewDialog,
  StartingChatMessage,
  StartingChatMessageCard,
  UploadedFile,
  UserMessageContextQuotes,
  useAutoScroll,
  useAutosizeTextArea,
  useChatFileManager,
  useUploadPdf,
} from '@eluve/blocks';
import {
  Button,
  Heading,
  Icon,
  Input,
  Link,
  LoadingIcon,
  Separator,
  SimpleTooltip,
  Stack,
  Text,
  Textarea,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from '@eluve/components';
import { insertDocumentReferenceMutation } from '@eluve/frontend-feature-appointment';
import { useAnalytics } from '@eluve/posthog-react';
import { EluveRenderer } from '@eluve/sdui-renderer';
import { type Spec } from '@eluve/sdui-types';
import { useUserIdFromSession } from '@eluve/session-helpers';

import { ChatMessageFeedback } from './ChatMessageFeedback';
import type { ChatView } from './useChatSession';

const INITIAL_VISIBLE_STARTERS = 5;

// Types

type ChatMessageWithSpec = {
  spec?: Spec | null;
  [key: string]: unknown;
};

type PasswordModalState = {
  isOpen: boolean;
  fileId: string | null;
  fileName: string | null;
};

type ChatProps = {
  tenantId: string;
  patientId?: string;
  appointmentId?: string;
  view: ChatView;
  currentThreadId: string | null;
  draftMessage: string;
  status?: 'submitted' | 'streaming' | 'ready' | 'error';
  onNewChat: () => void;
  onGoToHistory: () => void;
  onEnsureThread: () => Promise<string | null>;
  onMessageSend: (
    content?: string,
    preferenceId?: string,
    starterId?: string,
    specTemplateId?: string,
  ) => void;
  onSelectThread: (threadId: string) => void;
  onClose?: () => void;
  onStopStream?: () => void;
  setDraftMessage: (message: string) => void;
  startingMessages?: StartingChatMessage[];
  threads: ChatThread[];
  onThreadTitleUpdate?: (threadId: string, newTitle: string) => void;
  onStarterSaved?: () => void;
  onNoteUpdated?: () => void;
  onCustomInstructionSaved?: () => void;
  onDocumentAdded?: (doc?: { id: string; title: string }) => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
  /** PDF file manager state, typically from useChatSession */
  pdfManager?: PdfContextState;
  /** Handler for citation source clicks */
  onSourceClick?: (citation: Citation) => void;
};

// Components

export const Chat = ({
  tenantId,
  patientId,
  appointmentId,
  view,
  currentThreadId,
  draftMessage,
  status,
  onNewChat,
  onGoToHistory,
  onEnsureThread,
  onMessageSend,
  onSelectThread,
  onClose,
  onStopStream,
  setDraftMessage,
  startingMessages,
  threads,
  onThreadTitleUpdate,
  onStarterSaved,
  onNoteUpdated,
  onCustomInstructionSaved,
  onDocumentAdded,
  hasMore,
  isLoadingMore,
  onLoadMore,
  pdfManager: pdfManagerProp,
  onSourceClick,
}: ChatProps) => {
  const userId = useUserIdFromSession();
  const analytics = useAnalytics('chat');

  const [insertDocument] = useMutation(insertDocumentReferenceMutation);

  const handleDocumentCommit = async (
    draft: { title: string; content: string; docType?: string },
    edited: boolean,
  ) => {
    if (!patientId) return;

    const result = await insertDocument({
      variables: {
        object: {
          patientId,
          appointmentId: appointmentId ?? null,
          title: draft.title,
          content: draft.content,
          contentType: 'text/markdown',
          docType: draft.docType ?? null,
          status: 'current',
          authoredAt: new Date().toISOString(),
        },
      },
    });

    const doc = result.data?.insertDocumentReferencesOne;
    analytics.capture('document_generated', {
      document_id: doc?.id,
      origin: 'chat',
      tenant_id: tenantId,
      patient_id: patientId,
      appointment_id: appointmentId,
      doc_type: draft.docType,
      edited,
    });

    onDocumentAdded?.();
  };

  // Use pdfManager from props (provided by useChatSession via ChatComponent),
  // or create a minimal fallback for direct Chat usage.
  const internalPdfManager = useChatFileManager({
    tenantId,
    patientId: patientId ?? '',
    currentThreadId,
  });
  const pdfManager = pdfManagerProp ?? internalPdfManager;

  const [showAllStarters, setShowAllStarters] = useState(false);
  const [passwordModal, setPasswordModal] = useState<PasswordModalState>({
    isOpen: false,
    fileId: null,
    fileName: null,
  });

  const internalHandleSourceClick = useCallback(
    (citation: Citation) => {
      analytics.capture('chat_source_clicked', {
        tenantId,
        userId,
        sourceType: citation.source.sourceType,
        sourceId: citation.source.sourceDocId,
        threadId: currentThreadId,
      });
    },
    [analytics, tenantId, userId, currentThreadId],
  );
  const handleSourceClick = onSourceClick ?? internalHandleSourceClick;

  const handlePdfUploadComplete = (file: UploadedFile) => {
    pdfManager?.setUploadedFiles((prev) => [...prev, file]);

    analytics.capture('chat_thread_file_added', {
      tenantId,
      userId,
      patientId,
      threadId: currentThreadId,
      fileId: file.id,
      fileName: file.fileName,
      fileSize: file.fileSize,
    });

    // Show password modal if file has password
    if (file.hasPassword) {
      setPasswordModal({
        isOpen: true,
        fileId: file.id,
        fileName: file.fileName || null,
      });
    }
  };

  const fileInputRef = useRef<HTMLInputElement>(null);

  const { uploadFile, isUploading } = useUploadPdf({
    tenantId,
    threadId: currentThreadId ?? '',
    onUploadComplete: handlePdfUploadComplete,
  });

  const handleFileRemove = async (fileId: string) => {
    const fileToRemove = pdfManager?.uploadedFiles.find((f) => f.id === fileId);
    if (fileToRemove) {
      const { id: fileId, fileName, fileSize } = fileToRemove;
      analytics.capture('chat_thread_file_removed', {
        tenantId,
        userId,
        patientId,
        threadId: currentThreadId,
        fileId,
        fileName,
        fileSize,
      });
    }
    await pdfManager?.handleRemove(fileId);
  };

  const currentThread = threads?.find(
    (thread) => thread.id === currentThreadId,
  );
  const hasMessages =
    currentThread?.messages && currentThread.messages.length > 0;
  const isStreaming = status === 'streaming';
  const isPending = status === 'submitted';
  const isBusy = isStreaming || isPending;
  const isLoading = isStreaming || isUploading;

  const messageListRef = useAutoScroll([currentThread?.messages, isStreaming], {
    disengageThresholdPx: Infinity,
  });

  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  useAutosizeTextArea({
    ref: textAreaRef,
    maxHeight: 120,
    dependencies: [draftMessage],
  });

  useEffect(() => {
    if (currentThreadId && !isLoading) {
      textAreaRef.current?.focus();
    }
  }, [currentThreadId, isLoading]);

  const handleDraftMessageChange = (
    event: ChangeEvent<HTMLTextAreaElement>,
  ) => {
    setDraftMessage(event.target.value);
  };

  const handleDraftMessageKeyDown = (
    event: KeyboardEvent<HTMLTextAreaElement>,
  ) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      textAreaRef.current?.blur();
      event.preventDefault();
      handleSendMessageClick();
    } else if (event.key === 'Escape') {
      textAreaRef.current?.blur();
      event.preventDefault();
    }
  };

  const handleSendMessageClick = () => {
    if (draftMessage.length === 0) {
      return;
    }
    onMessageSend();
  };

  const handleStartingMessageSelect = (
    content: string,
    preferenceId?: string,
    starterId?: string,
    specTemplateId?: string,
  ) => {
    onMessageSend(content, preferenceId, starterId, specTemplateId);
  };

  const handleFileUpload = () => {
    analytics.capture('chat_file_picker_opened', {
      tenantId,
      userId,
      patientId,
      threadId: currentThreadId,
    });

    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleThreadTitleEditCancel = useCallback(() => {
    analytics.capture('chat_thread_rename_cancelled', {
      tenantId,
      userId,
      patientId,
      threadId: currentThreadId,
    });
  }, [analytics, tenantId, userId, patientId, currentThreadId]);

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !file.name.toLowerCase().endsWith('.pdf')) {
      event.target.value = '';
      return;
    }

    const threadId = await onEnsureThread();
    if (!threadId) {
      event.target.value = '';
      return;
    }

    await uploadFile?.(file, threadId);
    event.target.value = '';
  };

  const handleMessageCopy = useCallback(
    (message: ChatMessage) => {
      analytics.capture('chat_message_copied', {
        tenantId,
        userId,
        patientId,
        threadId: currentThreadId,
        messageId: message.id,
        messageRole: message.role,
        messageContent: message.content,
        wordCount: message.content?.trim()?.split(/\s+/)?.length,
      });
    },
    [analytics, tenantId, userId, patientId, currentThreadId],
  );

  return (
    <PdfContext.Provider value={pdfManager}>
      <AnimatePresence>
        <LayoutGroup>
          <Stack className="max-w-full" fullHeight>
            {view === 'history' && (
              <motion.div
                initial={{ x: -64, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: 64, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="flex size-full w-full max-w-full flex-1 flex-col"
              >
                <ChatHeader
                  isLoading={isLoading}
                  showHistoryButton={false}
                  onNewChat={onNewChat}
                  onClose={onClose}
                />

                <Stack className="flex-1 overflow-auto p-4" gap={4}>
                  {threads.map((thread) => (
                    <ChatThreadCard
                      key={thread.id}
                      thread={thread}
                      onSelectThread={onSelectThread}
                      onThreadTitleChange={onThreadTitleUpdate}
                    />
                  ))}
                  {hasMore && (
                    <Stack justify="center" gap={2} direction="horizontal">
                      <Button
                        variant="outline"
                        size="sm"
                        loading={isLoadingMore}
                        disabled={isLoadingMore}
                        onClick={onLoadMore}
                      >
                        Load more
                      </Button>
                    </Stack>
                  )}
                </Stack>
              </motion.div>
            )}
            {(view === 'new' || view === 'thread') && (
              <motion.div
                key="chat"
                initial={{ x: 64, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: -64, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="flex size-full w-full max-w-full flex-1 flex-col"
              >
                <ChatHeader
                  isLoading={isLoading}
                  showNewChatButton={view !== 'new'}
                  showHistoryButton={true}
                  onNewChat={onNewChat}
                  onGoToHistory={onGoToHistory}
                  onClose={onClose}
                  threadId={currentThreadId ?? undefined}
                  threadTitle={currentThread?.title}
                  onThreadTitleChange={onThreadTitleUpdate}
                  onThreadTitleEditCancel={handleThreadTitleEditCancel}
                />
                <AnimatePresence mode="wait" initial={false}>
                  <motion.div
                    key={currentThreadId ?? 'new'}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                    ref={hasMessages ? messageListRef : undefined}
                    className={cn(
                      'flex w-full flex-1 flex-col items-start overflow-y-auto p-4',
                      hasMessages ? 'gap-4' : 'gap-8',
                    )}
                  >
                    {hasMessages ? (
                      <>
                        {currentThread?.messages?.map((message, idx) => {
                          const isLastMessage =
                            idx === (currentThread.messages?.length ?? 0) - 1;
                          const isLastMessageStreaming =
                            isStreaming && isLastMessage;

                          return (
                            <Stack key={message.id} gap={2}>
                              {message.role === 'user' && (
                                <UserMessageContextQuotes
                                  contexts={message.contexts}
                                />
                              )}
                              <ChatMessageBubble
                                message={message}
                                isStreaming={isLastMessageStreaming}
                                templateMessages={startingMessages}
                                onMessageSend={onMessageSend}
                                onStarterSaved={onStarterSaved}
                                onNoteUpdated={onNoteUpdated}
                                onCustomInstructionSaved={
                                  onCustomInstructionSaved
                                }
                                onDocumentAdded={onDocumentAdded}
                                onDocumentCommit={handleDocumentCommit}
                                onCopy={handleMessageCopy}
                                onSourceClick={handleSourceClick}
                              >
                                {(message as ChatMessageWithSpec).spec && (
                                  <div className="mt-3 max-w-full min-w-0 overflow-hidden rounded-xl border border-border-primary bg-bg-primary p-4">
                                    <EluveRenderer
                                      spec={
                                        (message as ChatMessageWithSpec)
                                          .spec as Spec
                                      }
                                    />
                                  </div>
                                )}
                              </ChatMessageBubble>
                              {!isStreaming && message.role === 'assistant' && (
                                <Stack
                                  className="pl-4"
                                  gap={2}
                                  direction="horizontal"
                                >
                                  <ChatMessageFeedback messageId={message.id} />
                                </Stack>
                              )}
                              {!isLastMessageStreaming &&
                              message.citations?.length ? (
                                <Stack className="pl-4" gap={2}>
                                  <Separator className="my-6" />
                                  <CitationsSection
                                    citations={message.citations}
                                    onSourceClick={handleSourceClick}
                                  />
                                </Stack>
                              ) : null}
                            </Stack>
                          );
                        })}
                        {isPending && (
                          <div className="w-full self-start">
                            <Stack
                              justify="center"
                              className="space-x-1 p-4"
                              gap={2}
                            >
                              <div className="size-3 animate-pulse rounded-full bg-text-tertiary"></div>
                            </Stack>
                          </div>
                        )}
                      </>
                    ) : (
                      <Stack
                        className="my-auto px-6 py-4"
                        gap={8}
                        align="center"
                      >
                        <Heading
                          weight="medium"
                          size="2xl"
                          color="primary"
                          className="text-center"
                        >
                          Ask Eluve anything
                        </Heading>
                        {(showAllStarters
                          ? startingMessages
                          : startingMessages?.slice(0, INITIAL_VISIBLE_STARTERS)
                        )?.map((message) => (
                          <StartingChatMessageCard
                            key={message.id}
                            message={message}
                            onSelectMessage={handleStartingMessageSelect}
                          />
                        ))}
                        {(startingMessages?.length ?? 0) >
                          INITIAL_VISIBLE_STARTERS && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const next = !showAllStarters;
                              setShowAllStarters(next);
                              analytics.capture(
                                'chat_conversation_starters_expanded',
                                {
                                  tenantId,
                                  userId,
                                  patientId,
                                  threadId: currentThreadId,
                                  expanded: next,
                                },
                              );
                            }}
                          >
                            {showAllStarters ? 'Less' : 'More'}
                          </Button>
                        )}
                        <Text
                          size="sm"
                          color="tertiary"
                          className="text-center"
                        >
                          AI can make mistakes. Please double-check responses.
                          <br />
                          <br />
                          See{' '}
                          <Link
                            target="_blank"
                            to="https://www.eluve.com/terms-of-use"
                          >
                            T&C
                          </Link>{' '}
                          for more info.
                        </Text>
                      </Stack>
                    )}
                  </motion.div>
                </AnimatePresence>
                <Stack gap={3}>
                  {pdfManager.loading ? (
                    <LoadingIcon className="mx-3 h-[44px] py-2" />
                  ) : (
                    pdfManager.uploadedFiles.length > 0 && (
                      <Stack gap={2} align="start" wrap direction="horizontal">
                        {pdfManager.uploadedFiles.map((file: UploadedFile) => (
                          <SimpleTooltip
                            label={
                              file.hasPassword
                                ? 'This PDF is password protected. Click to unlock it!'
                                : 'Preview this PDF'
                            }
                            key={file.id}
                          >
                            <div
                              className="cursor-pointer border border-border-primary hover:bg-bg-secondary"
                              onClick={() => {
                                if (file.hasPassword) {
                                  setPasswordModal({
                                    isOpen: true,
                                    fileId: file.id,
                                    fileName: file.fileName || null,
                                  });
                                } else {
                                  pdfManager.handlePreview(file);
                                }
                              }}
                            >
                              <Stack
                                gap={2}
                                align="center"
                                direction="horizontal"
                                fullHeight
                              >
                                <div className="ml-2">
                                  {file.hasPassword ? (
                                    <Icon
                                      name="AlertCircle"
                                      size="lg"
                                      className="cursor-pointer text-bg-negative-strong-primary"
                                    />
                                  ) : (
                                    <Icon name="FileText" size="lg" />
                                  )}
                                </div>
                                <Text inline size="xs" className="truncate">
                                  {file.fileName}
                                </Text>
                                <Button
                                  variant="ghost"
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    await handleFileRemove(file.id);
                                  }}
                                  aria-label={`Remove ${file.fileName}`}
                                >
                                  <Icon name="X" size="auto" />
                                </Button>
                              </Stack>
                            </div>
                          </SimpleTooltip>
                        ))}
                      </Stack>
                    )
                  )}
                  <Input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf"
                    onChange={handleFileChange}
                    disabled={isUploading}
                    className="hidden"
                  />
                  <Stack
                    gap={1}
                    align="stretch"
                    className="w-full border-t border-border-primary p-3"
                  >
                    <Textarea
                      rows={1}
                      disabled={isBusy}
                      onChange={handleDraftMessageChange}
                      onKeyDown={handleDraftMessageKeyDown}
                      onSubmit={handleSendMessageClick}
                      placeholder="Ask anything"
                      ref={textAreaRef}
                      value={draftMessage}
                      className="min-h-8 resize-none border-0 p-2 focus-visible:ring-0 focus-visible:ring-offset-0"
                    />
                    <Stack justify="between" gap={2} direction="horizontal">
                      <TooltipProvider>
                        <Tooltip delayDuration={0}>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              loading={isUploading}
                              onClick={handleFileUpload}
                              disabled={isLoading || isUploading}
                              aria-label="Attach file"
                            >
                              <Icon name="Paperclip" size="auto" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Attach file</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      <div className="[&>button]:rounded-full">
                        {isBusy ? (
                          <Button
                            onClick={() => {
                              analytics.capture('chat_stream_stopped', {
                                tenantId,
                                userId,
                                patientId,
                                threadId: currentThreadId,
                              });
                              onStopStream?.();
                            }}
                            aria-label="Stop"
                          >
                            <Icon name="CircleStop" size="auto" />
                          </Button>
                        ) : (
                          <Button
                            onClick={handleSendMessageClick}
                            disabled={!draftMessage}
                            aria-label="Send message"
                          >
                            <Icon name="ArrowUp" size="auto" />
                          </Button>
                        )}
                      </div>
                    </Stack>
                  </Stack>
                </Stack>
              </motion.div>
            )}
          </Stack>
          <PdfPreviewDialog />
          <PdfPasswordModal
            isOpen={passwordModal.isOpen}
            fileId={passwordModal.fileId}
            fileName={passwordModal.fileName}
            tenantId={tenantId}
            threadId={currentThreadId ?? ''}
            onClose={() =>
              setPasswordModal({ isOpen: false, fileId: null, fileName: null })
            }
            onDecryptSuccess={() => {
              if (currentThreadId) {
                pdfManager?.loadThreadFiles(currentThreadId);
              }
            }}
          />
        </LayoutGroup>
      </AnimatePresence>
    </PdfContext.Provider>
  );
};
